import React, { useState, useEffect } from 'react';
import { StorageService } from '../services/storage';
import { APPS_DATA } from '../data/mockData';
import { AppData } from '../types';
import { StoreBadge } from '../components/StoreBadge';
import { AppIcon } from '../components/AppIcon';
import { Bookmark, Bell, Plus, Scale, Check } from 'lucide-react';
import { getGrowthClassification } from '../components/GrowthScoreGauge';

interface WatchlistViewProps {
  onSelectApp: (appId: string) => void;
  onCompare: (appIds: string[]) => void;
  onNavigate: (route: string) => void;
}

export const WatchlistView: React.FC<WatchlistViewProps> = ({
  onSelectApp,
  onCompare,
  onNavigate
}) => {
  const [watchlistIds, setWatchlistIds] = useState(StorageService.getWatchlist());
  const [alerts, setAlerts] = useState(StorageService.getAlerts());
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  useEffect(() => {
    const refresh = () => {
      setWatchlistIds(StorageService.getWatchlist());
      setAlerts(StorageService.getAlerts());
    };
    window.addEventListener('appstatsview_watchlist_updated', refresh);
    window.addEventListener('appstatsview_alerts_updated', refresh);
    return () => {
      window.removeEventListener('appstatsview_watchlist_updated', refresh);
      window.removeEventListener('appstatsview_alerts_updated', refresh);
    };
  }, []);

  const watchedApps: AppData[] = watchlistIds
    .map((id) => APPS_DATA[id])
    .filter(Boolean);

  const handleRemove = (appId: string) => {
    StorageService.toggleWatchlist(appId);
    setWatchlistIds(StorageService.getWatchlist());
    setToastMessage('Removed app from watchlist');
    setTimeout(() => setToastMessage(null), 2500);
  };

  const handleToggleAlert = (appId: string, appName: string) => {
    const existing = alerts.find((a) => a.appId === appId);
    if (existing) {
      StorageService.removeAlert(existing.id);
      setToastMessage(`Alert disabled for ${appName}`);
    } else {
      StorageService.addAlert({
        appId,
        appName,
        type: 'growth_spike',
        threshold: 85,
        isActive: true
      });
      setToastMessage(`Alert created: Notify when ${appName} triggers Growth Spike or Outlier!`);
    }
    setAlerts(StorageService.getAlerts());
    setTimeout(() => setToastMessage(null), 2500);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 space-y-8 animate-fadeIn">
      {/* Toast */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 bg-slate-900 text-white px-4 py-3 rounded-2xl shadow-xl border border-slate-700 text-xs font-bold flex items-center gap-2 animate-fadeIn">
          <Check className="w-4 h-4 text-emerald-400" strokeWidth={2.5} />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-6 border-b border-slate-200/80">
        <div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs font-bold mb-2">
            <Bookmark className="w-3.5 h-3.5" strokeWidth={1.75} />
            <span>Personalized Radar</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-950 tracking-tight">
            My App Watchlist
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Tracked applications with custom alerts on rank velocity, growth spikes, and store outlier anomalies.
          </p>
        </div>

        {watchedApps.length > 1 && (
          <button
            onClick={() => onCompare(watchedApps.map((a) => a.id).slice(0, 5))}
            className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs px-4 py-2.5 rounded-2xl shadow-md transition-colors"
          >
            <Scale className="w-3.5 h-3.5" strokeWidth={1.75} />
            <span>Compare All Tracked Apps</span>
          </button>
        )}
      </div>

      {watchedApps.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 text-center border border-slate-200/80 max-w-lg mx-auto space-y-4">
          <Bookmark className="w-12 h-12 text-slate-300 mx-auto" strokeWidth={1.5} />
          <h3 className="text-lg font-bold text-slate-900">Your Watchlist is Empty</h3>
          <p className="text-xs text-slate-500 leading-relaxed">
            Click the &ldquo;Track App&rdquo; button on any application page to start monitoring its daily movements and receive velocity alerts.
          </p>
          <button
            onClick={() => onNavigate('/trending')}
            className="bg-blue-600 text-white text-xs font-bold px-5 py-2.5 rounded-xl shadow-md"
          >
            Explore Trending Apps
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-100 text-slate-400 font-semibold uppercase tracking-wider text-[10px]">
                  <th className="pb-3 pl-2">Tracked Application</th>
                  <th className="pb-3 text-center">Rank</th>
                  <th className="pb-3 text-center">Growth Score</th>
                  <th className="pb-3 text-center">Est. Downloads</th>
                  <th className="pb-3 text-center">Alert Status</th>
                  <th className="pb-3 text-right pr-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-medium">
                {watchedApps.map((app) => {
                  const { label, bg } = getGrowthClassification(app.growthScore);
                  const hasAlert = alerts.some((a) => a.appId === app.id);

                  return (
                    <tr key={app.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-4 pl-2">
                        <div
                          onClick={() => onSelectApp(app.id)}
                          className="flex items-center gap-3 cursor-pointer group"
                        >
                          <AppIcon
                            app={app}
                            size="md"
                            className="w-11 h-11 rounded-2xl shadow-sm group-hover:scale-105 transition-transform flex-shrink-0"
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-slate-900 text-sm group-hover:text-blue-600 transition-colors">
                                {app.name}
                              </span>
                              <StoreBadge store={app.primaryStore} />
                            </div>
                            <span className="text-xs text-slate-400 block">{app.developer} • {app.category}</span>
                          </div>
                        </div>
                      </td>

                      <td className="py-4 text-center">
                        <span className="font-bold text-slate-900 bg-slate-100 px-2 py-0.5 rounded-md">
                          #{app.currentRank}
                        </span>
                      </td>

                      <td className="py-4 text-center">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${bg}`}>
                          {app.growthScore} • {label}
                        </span>
                      </td>

                      <td className="py-4 text-center font-semibold text-slate-700">
                        {(app.estimatedMonthlyDownloads / 1000000).toFixed(1)}M / mo
                      </td>

                      <td className="py-4 text-center">
                        <button
                          onClick={() => handleToggleAlert(app.id, app.name)}
                          className={`text-xs font-bold px-3 py-1.5 rounded-xl transition-all border ${
                            hasAlert
                              ? 'bg-amber-50 text-amber-800 border-amber-200'
                              : 'bg-slate-100 hover:bg-slate-200 text-slate-600 border-transparent'
                          }`}
                        >
                          {hasAlert ? (
                            <span className="inline-flex items-center gap-1.5">
                              <Bell className="w-3 h-3 text-amber-600" strokeWidth={2} />
                              <span>Active Alert</span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5">
                              <Plus className="w-3 h-3" strokeWidth={2} />
                              <span>Set Alert</span>
                            </span>
                          )}
                        </button>
                      </td>

                      <td className="py-4 text-right pr-2 space-x-3">
                        <button
                          onClick={() => onSelectApp(app.id)}
                          className="text-xs font-bold text-blue-600 hover:underline"
                        >
                          View Stats
                        </button>
                        <button
                          onClick={() => handleRemove(app.id)}
                          className="text-xs font-semibold text-slate-400 hover:text-rose-600"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
